/*
 OK FBI — Streamer Cue Cards (host-only, floating dock)
 - Always visible unless collapsed. Never rendered on Stream Mode overlay.
 - Host can navigate lines per phase, pin one, copy, edit, generate an alt, and
   optionally push a specific line to the OBS overlay via /session/cue-cards {broadcast}.
*/
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Copy, Pin, PinOff, Radio, RadioTower, Sparkles, Pencil, Trash2, Plus, EyeOff, Eye, MessageSquare } from "lucide-react";
import { DEFAULT_CUES, PHASES, PHASE_LABELS, mergeCues, generateAltCue, getLocalPrefs, saveLocalPrefs } from "@/lib/cueCards";
import { useSession } from "@/contexts/SessionContext";

const PHASE_ORDER = PHASES.map(p => p.key);

// Heuristic: infer current phase from session state (best-effort; host can override)
function inferPhase(session, overlay) {
  const stage = (overlay?.phase || session?.stage || "").toLowerCase();
  const inter = overlay?.intermission?.active;
  if (inter) return "intermission";
  if (session?.solution_revealed) {
    const res = ((session?.case_results || {})[session?.current_case_id] || {}).status;
    if (res === "solved") return "solved";
    if (res === "failed") return "failed";
    return "accusation";
  }
  if (stage === "voting") return "voting";
  if (session?.current_case_id) return "evidence";
  return "opening";
}

export function CueCardsDock() {
  const { session } = useSession();
  const [collapsed, setCollapsed] = useState(false);
  // Default CLOSED (host must click the button explicitly). Persisted in localStorage so it
  // stays closed while navigating between tabs / cases within the same session.
  const [visible, setVisibleState] = useState(() => {
    try { return typeof window !== "undefined" && window.localStorage.getItem("okfbi_cue_visible") === "1"; }
    catch { return false; }
  });
  const setVisible = (v) => {
    setVisibleState(v);
    try { if (typeof window !== "undefined") window.localStorage.setItem("okfbi_cue_visible", v ? "1" : "0"); } catch {}
  };
  const [phase, setPhase] = useState("opening");
  const [autoPhase, setAutoPhase] = useState(true);
  const [customByPhase, setCustomByPhase] = useState({});   // phase -> [texts]
  const [pinned, setPinnedState] = useState(null);          // {phase, index} | null
  const [cursor, setCursor] = useState({});                 // phase -> index
  const [editingIdx, setEditingIdx] = useState(null);       // idx in ALL lines (defaults + custom)
  const [editText, setEditText] = useState("");
  const [overlay, setOverlay] = useState(null);
  const [broadcastAt, setBroadcastAt] = useState(null);

  // Load cue_cards from backend + local prefs
  const loadServer = useCallback(async () => {
    try {
      const { data } = await api.get("/session/cue-cards");
      setCustomByPhase(data?.custom || {});
      setPinnedState(data?.pinned || null);
      if (data?.broadcast?.at) setBroadcastAt(data.broadcast.at);
    } catch {}
  }, []);
  useEffect(() => { loadServer(); }, [loadServer]);
  useEffect(() => {
    const p = getLocalPrefs();
    if (p.cursor) setCursor(p.cursor);
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get("/overlay/state");
        setOverlay(data);
        if (data?.cue_card?.at && data.cue_card.at !== broadcastAt) setBroadcastAt(data.cue_card.at);
      } catch {}
    }, 3000);
    return () => clearInterval(iv);
  }, []);

  // Auto-follow phase (host can pin manually)
  useEffect(() => {
    if (!autoPhase) return;
    const p = inferPhase(session, overlay);
    if (p !== phase) setPhase(p);
  }, [session?.stage, session?.current_case_id, session?.solution_revealed, overlay?.phase, overlay?.intermission?.active, autoPhase]);

  // Stream Mode → hide entirely (never leak host-only text on stream)
  // (Guard must come AFTER all hooks to comply with rules-of-hooks)
  const all = useMemo(() => mergeCues(customByPhase), [customByPhase]);
  if (session?.stream_mode) return null;

  const lines = all[phase] || [];
  const numDefaults = (DEFAULT_CUES[phase] || []).length;
  const idx = pinned && pinned.phase === phase ? pinned.index : (cursor[phase] ?? 0);
  const clamped = Math.max(0, Math.min(lines.length - 1, idx));
  const line = lines[clamped] || "";
  const isPinnedHere = pinned && pinned.phase === phase && pinned.index === clamped;
  const isCustom = clamped >= numDefaults;

  const persistCursor = (nc) => { setCursor(nc); saveLocalPrefs({ ...getLocalPrefs(), cursor: nc }); };

  const nav = (delta) => {
    if (!lines.length) return;
    const nxt = (clamped + delta + lines.length) % lines.length;
    persistCursor({ ...cursor, [phase]: nxt });
  };
  const pinCurrent = async () => {
    const val = isPinnedHere ? null : { phase, index: clamped };
    setPinnedState(val);
    await api.patch("/session/cue-cards", { pinned: val || {} }).catch(() => {});
    toast.success(val ? "تم التثبيت" : "تم فك التثبيت");
  };
  const copyLine = async () => {
    try { await navigator.clipboard.writeText(line); toast.success("تم النسخ"); } catch { toast.error("تعذّر النسخ"); }
  };
  const beginEdit = () => { setEditingIdx(clamped); setEditText(line); };
  const cancelEdit = () => { setEditingIdx(null); setEditText(""); };
  const saveEdit = async () => {
    const t = editText.trim(); if (!t) { cancelEdit(); return; }
    const cur = { ...customByPhase };
    const arr = [...(cur[phase] || [])];
    if (editingIdx < numDefaults) {
      // editing a default → append as a new custom line, and switch cursor to it
      arr.push(t);
      cur[phase] = arr;
      setCustomByPhase(cur);
      await api.patch("/session/cue-cards", { custom: cur }).catch(() => {});
      persistCursor({ ...cursor, [phase]: numDefaults + arr.length - 1 });
    } else {
      const ci = editingIdx - numDefaults;
      arr[ci] = t;
      cur[phase] = arr;
      setCustomByPhase(cur);
      await api.patch("/session/cue-cards", { custom: cur }).catch(() => {});
    }
    cancelEdit();
    toast.success("تم الحفظ");
  };
  const deleteCustom = async () => {
    if (!isCustom) return;
    const cur = { ...customByPhase };
    const ci = clamped - numDefaults;
    const arr = [...(cur[phase] || [])];
    arr.splice(ci, 1);
    cur[phase] = arr;
    setCustomByPhase(cur);
    await api.patch("/session/cue-cards", { custom: cur }).catch(() => {});
    persistCursor({ ...cursor, [phase]: Math.max(0, clamped - 1) });
    toast.success("تم الحذف");
  };
  const generateAlt = async () => {
    const t = generateAltCue(phase);
    const cur = { ...customByPhase };
    const arr = [...(cur[phase] || []), t];
    cur[phase] = arr;
    setCustomByPhase(cur);
    await api.patch("/session/cue-cards", { custom: cur }).catch(() => {});
    persistCursor({ ...cursor, [phase]: numDefaults + arr.length - 1 });
    toast.success("تم توليد نص بديل");
  };
  const isBroadcasting = overlay?.cue_card?.text === line;
  const pushBroadcast = async () => {
    if (isBroadcasting) {
      await api.patch("/session/cue-cards", { broadcast: {} }).catch(() => {});
      toast.success("تم إخفاء النص من البث");
    } else {
      await api.patch("/session/cue-cards", { broadcast: { text: line, at: new Date().toISOString() } }).catch(() => {});
      toast.success("النص ظاهر الآن على البث");
    }
  };

  if (!visible) {
    return (
      <button onClick={() => setVisible(true)} data-testid="cue-cards-open"
        className="fixed bottom-4 left-4 z-40 border border-gold text-gold bg-black/80 backdrop-blur-sm px-3 py-1.5 text-[11px] font-cairo hover:bg-[rgba(197,160,89,0.15)] flex items-center gap-2 shadow-lg">
        <MessageSquare size={13} /> مساعد المقدم
      </button>
    );
  }

  return (
    <div
      dir="rtl"
      data-testid="cue-cards-dock"
      className="fixed bottom-4 left-4 z-40 w-[420px] max-w-[92vw] bg-gradient-to-b from-[rgba(10,10,12,0.94)] to-[rgba(4,4,6,0.94)] backdrop-blur-md border border-gold/40 shadow-[0_10px_40px_rgba(0,0,0,0.6)] font-cairo select-text"
      style={{ boxShadow: "0 10px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(197,160,89,0.15)" }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-metal">
        <div className="flex items-center gap-2 min-w-0">
          <MessageSquare size={13} className="text-gold shrink-0" />
          <div className="text-[10px] tracking-[0.35em] font-mono-ar text-gold">CUE · مساعد المقدم</div>
          {isBroadcasting && <span className="text-[9px] font-mono-ar text-red-400 border border-red-800 px-1.5 py-0.5 flex items-center gap-1"><RadioTower size={9} className="animate-pulse" /> LIVE</span>}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setCollapsed(c => !c)} data-testid="cue-cards-collapse"
            className="text-[9px] font-mono-ar text-zinc-400 hover:text-gold px-1.5 py-0.5">{collapsed ? "توسيع" : "طيّ"}</button>
          <button onClick={() => setVisible(false)} data-testid="cue-cards-close"
            className="w-6 h-6 border border-metal text-zinc-500 hover:border-red-800 hover:text-red-400 flex items-center justify-center">
            <EyeOff size={10} />
          </button>
        </div>
      </div>
      {!collapsed && (
        <>
          {/* Phase strip */}
          <div className="px-2 py-1.5 border-b border-metal flex items-center gap-1.5 flex-wrap" data-testid="cue-cards-phase-strip">
            <button onClick={() => setAutoPhase(a => !a)} data-testid="cue-cards-auto"
              className={`text-[9px] font-mono-ar px-1.5 py-0.5 border ${autoPhase ? "border-emerald-700 text-emerald-400" : "border-metal text-zinc-500 hover:text-white"}`}>
              {autoPhase ? "تلقائي" : "يدوي"}
            </button>
            {PHASE_ORDER.map(pk => (
              <button key={pk} onClick={() => { setAutoPhase(false); setPhase(pk); }}
                data-testid={`cue-cards-phase-${pk}`}
                className={`text-[10px] font-cairo px-2 py-0.5 border ${phase === pk ? "border-gold text-gold bg-[rgba(197,160,89,0.10)]" : "border-metal/60 text-zinc-400 hover:border-gold/50 hover:text-white"}`}>
                {PHASE_LABELS[pk]}
              </button>
            ))}
          </div>
          {/* Line viewer */}
          <div className="p-3">
            {editingIdx === null ? (
              <div className="text-[15px] leading-relaxed text-white border-r-2 border-gold pr-3" data-testid="cue-cards-line">
                {line || <span className="text-zinc-500 text-sm">لا توجد نصوص لهذه المرحلة.</span>}
              </div>
            ) : (
              <div>
                <textarea value={editText} onChange={(e) => setEditText(e.target.value)}
                  data-testid="cue-cards-edit-textarea"
                  className="w-full bg-black/40 border border-gold p-2 text-[13px] text-white outline-none min-h-[80px] font-cairo"
                  autoFocus />
                <div className="flex justify-end gap-1 mt-2">
                  <button onClick={cancelEdit} data-testid="cue-cards-edit-cancel"
                    className="text-[11px] px-3 py-1 border border-metal text-zinc-400 hover:text-white">إلغاء</button>
                  <button onClick={saveEdit} data-testid="cue-cards-edit-save"
                    className="text-[11px] px-3 py-1 border border-gold text-gold hover:bg-[rgba(197,160,89,0.15)]">حفظ</button>
                </div>
              </div>
            )}
            <div className="flex items-center justify-between mt-2 text-[9px] font-mono-ar text-zinc-500">
              <span>{clamped + 1} / {lines.length}</span>
              <span>{isCustom ? "نص مخصص" : "نص افتراضي"}</span>
            </div>
          </div>
          {/* Actions */}
          {editingIdx === null && (
            <div className="px-2 pb-2 flex flex-wrap items-center gap-1" data-testid="cue-cards-actions">
              <button onClick={() => nav(-1)} data-testid="cue-cards-prev" title="السابق"
                className="w-8 h-8 border border-metal text-zinc-300 hover:border-gold hover:text-gold flex items-center justify-center"><ChevronRight size={12} /></button>
              <button onClick={() => nav(1)} data-testid="cue-cards-next" title="التالي"
                className="w-8 h-8 border border-metal text-zinc-300 hover:border-gold hover:text-gold flex items-center justify-center"><ChevronLeft size={12} /></button>
              <button onClick={pinCurrent} data-testid="cue-cards-pin"
                className={`text-[11px] px-2 py-1 border flex items-center gap-1 ${isPinnedHere ? "border-gold text-gold bg-[rgba(197,160,89,0.15)]" : "border-metal text-zinc-300 hover:border-gold hover:text-gold"}`}>
                {isPinnedHere ? <><PinOff size={11} /> فك التثبيت</> : <><Pin size={11} /> تثبيت</>}
              </button>
              <button onClick={copyLine} data-testid="cue-cards-copy"
                className="text-[11px] px-2 py-1 border border-metal text-zinc-300 hover:border-gold hover:text-gold flex items-center gap-1"><Copy size={11} /> نسخ</button>
              <button onClick={beginEdit} data-testid="cue-cards-edit"
                className="text-[11px] px-2 py-1 border border-metal text-zinc-300 hover:border-gold hover:text-gold flex items-center gap-1"><Pencil size={11} /> تعديل</button>
              <button onClick={generateAlt} data-testid="cue-cards-generate"
                className="text-[11px] px-2 py-1 border border-metal text-zinc-300 hover:border-gold hover:text-gold flex items-center gap-1"><Sparkles size={11} /> توليد بديل</button>
              {isCustom && (
                <button onClick={deleteCustom} data-testid="cue-cards-delete"
                  className="text-[11px] px-2 py-1 border border-red-900 text-red-400 hover:bg-red-950/30 flex items-center gap-1"><Trash2 size={11} /> حذف</button>
              )}
              <button onClick={pushBroadcast} data-testid="cue-cards-broadcast"
                className={`text-[11px] px-2 py-1 border flex items-center gap-1 ${isBroadcasting ? "border-red-700 text-red-400 bg-red-950/30" : "border-metal text-zinc-300 hover:border-gold hover:text-gold"}`}>
                <Radio size={11} /> {isBroadcasting ? "إخفاء من البث" : "إظهار النص على البث"}
              </button>
            </div>
          )}
          {/* Footer */}
          <div className="border-t border-metal px-3 py-1.5 text-[9px] font-mono-ar text-zinc-600 leading-tight">
            نصوص المقدم مخصصة للاستخدام الشخصي فقط · لا تظهر على البث إلا إذا اخترت ذلك يدوياً.
          </div>
        </>
      )}
    </div>
  );
}
